/**
 * TRACI-431: tell the agent when the shopper is on a different page from the one
 * their last message was sent from.
 *
 * Every message already carries the CURRENT page's context, but the agent's history
 * is full of the old page's context and its own answers about the old tire, so
 * "what about this one?" was read against the page the shopper had left. The fix is
 * to remember the page each message was sent from and, when the next one goes out
 * from somewhere else, send the page they left alongside it. The backend turns that
 * into a one-turn note in the prompt.
 *
 * Remembered per TAB (sessionStorage), never shared (localStorage): two tabs on two
 * pages sending into one conversation would otherwise flip the "last page" back and
 * forth and tell the agent the shopper moved on every message. sessionStorage
 * survives the full page loads of one tab, which is the case that matters. Keyed by
 * conversation, so a new or cleared conversation never inherits a "previous page".
 */
import type { PageContext } from './RealChatAgentService';

export interface PreviousPage {
  url: string;
  page_type: string | null;
  tire_name: string | null;
}

interface SentPage extends PreviousPage {
  sessionId: string;
}

/** The URL minus its hash: an in-page #reviews jump is the same page. */
export function pageKey(url: string): string {
  const i = url.indexOf('#');
  return i === -1 ? url : url.slice(0, i);
}

function summarize(pc: PageContext): PreviousPage | null {
  if (!pc.page_url) return null;
  return {
    url: pc.page_url,
    page_type: pc.page_kind ?? null,
    tire_name: pc.tire_name ?? null,
  };
}

/**
 * This tab's sessionStorage, or null where it is blocked. Reading the global itself
 * throws (SecurityError) in a cross-origin iframe with storage blocked, so it is never
 * touched bare: the note is a nicety and must never fail a send.
 */
export function tabStorage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

function read(storage: Storage | null, key: string): SentPage | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const v = JSON.parse(raw) as SentPage;
    return v && typeof v.url === 'string' && typeof v.sessionId === 'string' ? v : null;
  } catch {
    return null;
  }
}

/**
 * The page this conversation's last message was sent from, when the shopper is on
 * a different one now; otherwise null (first message, same page, unknown page).
 */
export function previousPageFor(
  storage: Storage | null,
  key: string,
  sessionId: string | undefined,
  pc: PageContext,
): PreviousPage | null {
  if (!sessionId) return null;
  const now = summarize(pc);
  const last = read(storage, key);
  if (!now || !last || last.sessionId !== sessionId) return null;
  if (pageKey(last.url) === pageKey(now.url)) return null;
  return { url: last.url, page_type: last.page_type, tire_name: last.tire_name };
}

/** Record the page a message was just sent from. Call only once the send succeeded. */
export function rememberSentPage(storage: Storage | null, key: string, sessionId: string | undefined, pc: PageContext): void {
  const now = summarize(pc);
  if (!storage || !sessionId || !now) return;
  try {
    storage.setItem(key, JSON.stringify({ sessionId, ...now } satisfies SentPage));
  } catch {
    /* quota or blocked storage: the note is a nicety, never a failure */
  }
}

export function forgetSentPage(storage: Storage | null, key: string): void {
  try {
    storage?.removeItem(key);
  } catch {
    /* blocked storage */
  }
}
